import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export const QUOTE_REFUSAL_REASONS = [
  { value: 'price_too_high', label: 'Le prix est trop élevé' },
  { value: 'no_longer_needed', label: 'Je ne souhaite plus réaliser les travaux' },
  { value: 'other_solution', label: "J'ai trouvé une autre solution" },
  { value: 'want_another_quote', label: 'Je souhaite un autre devis' },
  { value: 'other', label: 'Autre' },
];

const formatPrice = (price) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(price) || 0);

const QuoteRefuseModal = ({ open, quote, onClose, onConfirm, loading = false }) => {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setComment('');
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!reason) {
      setError('Veuillez sélectionner un motif de refus.');
      return;
    }
    setError('');
    const reasonLabel =
      QUOTE_REFUSAL_REASONS.find((r) => r.value === reason)?.label || reason;
    await onConfirm({
      status: 'rejected',
      refusal_reason: reason,
      refusal_reason_label: reasonLabel,
      refusal_comment: comment.trim() || null,
      refused_at: new Date().toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={loading ? undefined : onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-refuse-title"
        className="relative w-full max-w-md bg-card rounded-xl border border-border shadow-xl p-5 sm:p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id="quote-refuse-title" className="text-lg font-semibold text-foreground">
              Refus du devis
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Vous êtes sur le point de refuser ce devis d&apos;un montant de{' '}
              <strong className="text-foreground">{formatPrice(quote?.total_ttc)}</strong> TTC.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Souhaitez-vous nous indiquer pourquoi ?
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Fermer"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <fieldset className="space-y-2 mb-4">
          <legend className="sr-only">Motif du refus</legend>
          {QUOTE_REFUSAL_REASONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                reason === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/40'
              }`}
            >
              <input
                type="radio"
                name="quote-refusal-reason"
                value={option.value}
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
                className="mt-0.5"
                disabled={loading}
              />
              <span className="text-sm text-foreground">{option.label}</span>
            </label>
          ))}
        </fieldset>

        <div className="mb-4">
          <label htmlFor="quote-refusal-comment" className="block text-sm font-medium text-foreground mb-1.5">
            Commentaire (facultatif)
          </label>
          <textarea
            id="quote-refusal-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            disabled={loading}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Précisez si besoin…"
          />
        </div>

        {error && <p className="text-sm text-error mb-3">{error}</p>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleConfirm}
            disabled={loading}
            loading={loading}
          >
            Confirmer le refus
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteRefuseModal;
